import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { QrCode, Search, UserPlus, UserMinus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QRAssignPage() {
  const { user } = useAuth();
  const isSupervisor = user?.role === 'supervisor';
  const Layout = isSupervisor ? SupervisorLayout : AdminLayout;
  const [cards, setCards] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reassign, setReassign] = useState(null);
  const [pickSearch, setPickSearch] = useState('');
  const [removeCard, setRemoveCard] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const empUrl = isSupervisor ? `${API}/supervisor/employees` : `${API}/admin/employees`;
      const [cardsRes, empRes, supRes] = await Promise.all([
        axios.get(`${API}/admin/staff-qr-cards`),
        axios.get(empUrl),
        axios.get(`${API}/supervisor/supervisors`).catch(() => ({ data: [] })),
      ]);
      setCards((cardsRes.data || []).filter((c) => c.status === 'assigned' && c.current_employee_id));
      const emps = (empRes.data || []).filter((e) => e.status === 'active');
      const sups = (supRes.data || []).filter((s) => s.status === 'active').map((s) => ({ ...s, is_supervisor: true }));
      setStaff([...emps, ...sups].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch {
      toast.error('Failed to load QR assignments');
    } finally {
      setLoading(false);
    }
  }, [isSupervisor]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) =>
      (c.current_employee_name || '').toLowerCase().includes(q)
      || (c.card_code || '').toLowerCase().includes(q));
  }, [cards, search]);

  const doReassign = async (emp) => {
    try {
      await axios.post(`${API}/admin/staff-qr-cards/${reassign.id}/assign`, {
        employee_id: emp.id,
        assignment_type: reassign.assignment_type || 'permanent',
        event_id: reassign.event_id || null,
        event_label: reassign.event_label || null,
      });
      toast.success(`${reassign.card_code} → ${emp.name}`);
      setReassign(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    }
  };

  const doRemove = async () => {
    try {
      await axios.post(`${API}/admin/staff-qr-cards/${removeCard.id}/unassign`);
      toast.success('Assignment removed');
      setRemoveCard(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="qr-assign-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase">QR Assign</h1>
          <p className="text-sm text-muted-foreground">Staff currently holding a feedback QR card.</p>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or card code…"
            className="pl-10 bg-background border-border rounded-sm" data-testid="qr-assign-search" />
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <Card className="border-border"><CardContent className="py-10 text-center text-sm text-muted-foreground">
            No staff with an assigned QR card yet. Assign cards from Feedback → QR Cards.
          </CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="qr-assign-list">
            {filtered.map((c) => (
              <Card key={c.id} className="border-border" data-testid={`qr-assign-row-${c.card_code}`}>
                <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3">
                  <QrCode className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-mono text-sm font-semibold">{c.card_code}</span>
                  <span className="text-sm">{c.current_employee_name}</span>
                  <Badge variant="outline" className="text-[10px] uppercase rounded-sm">
                    {c.assignment_type === 'event_only' ? (c.event_label || 'Event') : 'Permanent'}
                  </Badge>
                  {c.assigned_at && (
                    <span className="text-xs text-muted-foreground">
                      since {format(parseISO(c.assigned_at), 'dd MMM yyyy')}
                    </span>
                  )}
                  <div className="ml-auto flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => { setReassign(c); setPickSearch(''); }}
                      className="rounded-sm text-xs border-primary/30 text-primary hover:bg-primary/10"
                      data-testid={`qr-assign-reassign-${c.card_code}`}>
                      <UserPlus className="w-3 h-3 mr-1" />Reassign
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoveCard(c)}
                      className="rounded-sm text-xs text-red-400 hover:bg-red-500/10"
                      data-testid={`qr-assign-remove-${c.card_code}`}>
                      <UserMinus className="w-3 h-3 mr-1" />Remove
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={!!reassign} onOpenChange={(o) => !o && setReassign(null)}>
          <DialogContent className="max-w-sm" data-testid="qr-assign-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">
                Reassign {reassign?.card_code} (now: {reassign?.current_employee_name}) to:
              </DialogTitle>
            </DialogHeader>
            <Input autoFocus placeholder="Search employee…" value={pickSearch}
              onChange={(e) => setPickSearch(e.target.value)} className="h-8 text-sm" data-testid="qr-assign-pick-filter" />
            <div className="max-h-60 overflow-y-auto space-y-1">
              {staff
                .filter((s) => s.id !== reassign?.current_employee_id
                  && (s.name || '').toLowerCase().includes(pickSearch.toLowerCase()))
                .map((s) => (
                  <button key={s.id} onClick={() => doReassign(s)}
                    className="w-full text-left px-3 py-2 text-sm rounded-sm border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
                    data-testid={`qr-assign-pick-${s.id}`}>
                    {s.name}{s.is_supervisor ? ' — Supervisor' : ''}
                    {(s.email || s.employee_code) && (
                      <span className="block text-[11px] text-muted-foreground">{s.email || s.employee_code}</span>
                    )}
                  </button>
                ))}
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!removeCard} onOpenChange={(o) => !o && setRemoveCard(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove assignment?</AlertDialogTitle>
              <AlertDialogDescription>
                {removeCard?.card_code} will become available again. The card link stays the same.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doRemove} data-testid="qr-assign-remove-confirm">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
